/** Cooling System 2 — React Query hooks */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    MockChiller,
    MockPlant,
    MockPowerContract,
    MockPump,
    MockCoolingSystem,
    EnergyContractsData,
} from './types';
import type { ViewerId } from './permissions';

// ── Query keys ──────────────────────────────────────────────────────────

const QUERY_KEYS = {
    plants: ['cooling-system-2', 'plants'] as const,
    chillers: ['cooling-system-2', 'chillers'] as const,
    pumps: ['cooling-system-2', 'pumps'] as const,
    powerContracts: ['cooling-system-2', 'power-contracts'] as const,
    coolingSystems: ['cooling-system-2', 'cooling-systems'] as const,
    energyContracts: ['cooling-system-2', 'energy-contracts'] as const,
};

// ── Queries ─────────────────────────────────────────────────────────────

export function usePlants() {
    return useQuery<MockPlant[]>({
        queryKey: QUERY_KEYS.plants,
        queryFn: async () => {
            const response = await fetch('/api/cooling-system-2/plants');
            if (!response.ok) throw new Error('Failed to fetch plants');
            return response.json();
        },
    });
}

export function useChillers() {
    return useQuery<MockChiller[]>({
        queryKey: QUERY_KEYS.chillers,
        queryFn: async () => {
            const response = await fetch('/api/cooling-system-2/chillers');
            if (!response.ok) throw new Error('Failed to fetch chillers');
            return response.json();
        },
    });
}

export function usePumps() {
    return useQuery<MockPump[]>({
        queryKey: QUERY_KEYS.pumps,
        queryFn: async () => {
            const response = await fetch('/api/cooling-system-2/pumps');
            if (!response.ok) throw new Error('Failed to fetch pumps');
            return response.json();
        },
    });
}

export function usePowerContracts() {
    return useQuery<MockPowerContract[]>({
        queryKey: QUERY_KEYS.powerContracts,
        queryFn: async () => {
            const response = await fetch('/api/cooling-system-2/power-contracts');
            if (!response.ok) throw new Error('Failed to fetch power contracts');
            return response.json();
        },
    });
}

export function useCoolingSystems() {
    return useQuery<MockCoolingSystem[]>({
        queryKey: QUERY_KEYS.coolingSystems,
        queryFn: async () => {
            const response = await fetch('/api/cooling-system-2/cooling-systems');
            if (!response.ok) throw new Error('Failed to fetch cooling systems');
            return response.json();
        },
    });
}

export function useEnergyContracts() {
    return useQuery<EnergyContractsData>({
        queryKey: QUERY_KEYS.energyContracts,
        queryFn: async () => {
            const response = await fetch('/api/cooling-system-2/energy-contracts');
            if (!response.ok) throw new Error('Les contrats d’énergies n’ont pas pu être chargés');
            return response.json();
        },
    });
}

// ── Mutations ───────────────────────────────────────────────────────────

export function useCreateChiller() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: Partial<MockChiller>) => {
            const response = await fetch('/api/cooling-system-2/chillers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create chiller');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chillers });
        },
    });
}

export function useUpdateNode(viewer: ViewerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ nodeId, data }: { nodeId: string; data: Record<string, unknown> }) => {
            const response = await fetch(`/api/cooling-system-2/nodes/${nodeId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'X-Viewer': viewer },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const payload = (await response.json().catch(() => null)) as { detail?: string } | null;
                throw new Error(payload?.detail ?? 'La configuration n’a pas pu être enregistrée');
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chillers });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pumps });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.powerContracts });
        },
    });
}

export function useDeleteNode(viewer: ViewerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (nodeId: string) => {
            const response = await fetch(`/api/cooling-system-2/nodes/${nodeId}`, {
                method: 'DELETE',
                headers: { 'X-Viewer': viewer },
            });
            if (!response.ok) {
                const payload = (await response.json().catch(() => null)) as { detail?: string } | null;
                throw new Error(payload?.detail ?? 'L’entité n’a pas pu être supprimée');
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chillers });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pumps });
        },
    });
}
